import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, MapPin, BedDouble, Maximize, Building2 } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Spinner, EmptyState } from "@/components/shared";

export default function PropertyDetailPage() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => { api.get(`/properties/${slug}`).then((r) => setP(r.data)).catch(() => setNotFound(true)); }, [slug]);

  if (notFound) return <div className="propora-container py-16"><EmptyState title="Property not found" /></div>;
  if (!p) return <Spinner />;

  return (
    <div className="propora-container py-10" data-testid="property-detail-page">
      <Link to="/properties" className="inline-flex items-center gap-1.5 text-sm text-[#4A5568] hover:text-[#007F83] mb-6"><ArrowLeft size={16} /> Back to properties</Link>
      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <div className="rounded-3xl overflow-hidden shadow-soft h-[360px] bg-[#E8F5F1]">
            <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
          </div>
          <div className="mt-6">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-[#007F83] bg-[#E8F5F1] px-2 py-0.5 rounded">{p.status}</span>
            <h1 className="font-display font-extrabold text-3xl text-[#0B1733] mt-3">{p.name}</h1>
            <p className="text-[#4A5568] inline-flex items-center gap-1 mt-2"><MapPin size={15} /> {p.location}</p>
            <div className="mt-4 flex items-center gap-6 text-sm text-[#4A5568]">
              {p.bedrooms > 0 && <span className="inline-flex items-center gap-1.5"><BedDouble size={16} /> {p.bedrooms} bedrooms</span>}
              {p.area_sqft && <span className="inline-flex items-center gap-1.5"><Maximize size={16} /> {p.area_sqft} sqft</span>}
            </div>
            <p className="mt-5 text-[#4A5568] leading-relaxed">{p.description}</p>
            {p.amenities?.length > 0 && (
              <>
                <h3 className="font-display font-bold text-lg text-[#0B1733] mt-7 mb-3">Amenities</h3>
                <div className="flex flex-wrap gap-2">{p.amenities.map((a) => <span key={a} className="text-sm bg-[#E8F5F1] text-[#0B1733] px-3 py-1.5 rounded-full">{a}</span>)}</div>
              </>
            )}
          </div>
        </div>
        <div>
          <div className="bg-white rounded-3xl border border-[#E3E8E8] p-6 shadow-soft sticky top-24">
            <p className="text-sm text-[#4A5568]">Indicative asking price</p>
            <p className="font-display font-extrabold text-2xl text-[#0B1733] mt-1">{p.asking_price}</p>
            <p className="text-xs text-[#4A5568] mt-1">Published by {p.publisher}</p>
            {p.project && (
              <Link to={`/projects/${p.project.slug}`} className="mt-4 flex items-center gap-2 text-sm text-[#007F83] font-medium hover:underline">
                <Building2 size={15} /> View project: {p.project.name}
              </Link>
            )}
            <Link to="/post-a-job"><Button className="w-full mt-5 rounded-full bg-[#007F83] hover:bg-[#006B70] text-white h-11" data-testid="property-post-job-btn">Need a service here? Post a job</Button></Link>
            <p className="mt-3 text-[11px] text-[#4A5568]">Example content — not a live listing. Real&State does not provide live DLD data or transaction figures.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
